// Over-the-air dictionary data updates.
//
// The data pipeline (hans_wehr_data → `python3 -m release.publish vN`) uploads each
// dataset version as an immutable, content-addressed snapshot plus a small manifest:
//
//   /dict/manifest.json  { schema, version, sha256, url, url_gz, entries, label, diff_summary, … }
//   /dict/hans_wehr.vN.<sha8>.json(.gz)
//
// (/dict/* is rewritten to the CDN in vercel.json, so web is same-origin and the native
// apps fetch https://hanswehr.app/dict/*.) On startup (idle, at most every 6 h) and when the
// native app resumes, we fetch the manifest; if its sha256 differs from the installed data
// — newer OR a rollback — we download, verify and install it into the inactive store, then
// flip. Nothing here blocks a lookup; any failure just leaves the current data in place.
/* global DecompressionStream */
import { Capacitor } from "@capacitor/core";
import { getDictionaryState, installDictionaryData } from "./dictionary-db";

export const SUPPORTED_SCHEMA = 1;
const CHECK_INTERVAL_MS = 6 * 60 * 60 * 1000;
const LAST_CHECK_KEY = "hw-dict-last-check";
const FETCH_TIMEOUT_MS = 20000;
const DOWNLOAD_TIMEOUT_MS = 120000;
const REQUIRED_KEYS = ["word_ID", "root", "forms", "nouns"];

export function dictBaseUrl() {
  if (process.env.REACT_APP_DICT_BASE_URL) return process.env.REACT_APP_DICT_BASE_URL;
  return Capacitor.isNativePlatform() ? "https://hanswehr.app/dict/" : "/dict/";
}

let inFlight = null;

/**
 * Checks for (and installs) a new dictionary version. Resolves to
 * { updated: boolean, reason, state? }. Never throws.
 */
export function checkForDictionaryUpdate({ force = false, notify = true } = {}) {
  if (inFlight) return inFlight;
  inFlight = run(force, notify)
    .catch((err) => {
      console.warn("Dictionary update failed:", err);
      return { updated: false, reason: `error: ${err?.message || err}` };
    })
    .finally(() => {
      inFlight = null;
    });
  return inFlight;
}

async function run(force, notify) {
  if (!force) {
    if (typeof navigator !== "undefined" && navigator.onLine === false) return skip("offline");
    if (navigator?.connection?.saveData) return skip("save-data");
    const last = Number(readLS(LAST_CHECK_KEY) || 0);
    if (Date.now() - last < CHECK_INTERVAL_MS) return skip("checked recently");
  }
  const base = dictBaseUrl();
  const manifest = await fetchJSON(new URL("manifest.json", absolute(base)).href, FETCH_TIMEOUT_MS);
  writeLS(LAST_CHECK_KEY, String(Date.now()));
  if (!manifest || !manifest.sha256 || !manifest.url) return skip("no manifest");
  if (manifest.schema > SUPPORTED_SCHEMA) return skip(`schema ${manifest.schema} unsupported`);

  const current = await getDictionaryState();
  if (current?.sha256 === manifest.sha256) return skip("up to date");

  const data = await download(manifest, absolute(base));
  validate(data, manifest);
  const state = await installDictionaryData(data, manifest);
  console.log(`📚 Dictionary updated to ${manifest.version} (${data.length} roots)`);
  if (notify) announce(manifest, current);
  return { updated: true, reason: "installed", state };
}

const skip = (reason) => ({ updated: false, reason });

function absolute(base) {
  return new URL(base, typeof window !== "undefined" ? window.location.href : "https://hanswehr.app/").href;
}

async function fetchWithTimeout(url, ms, init = {}) {
  const ctrl = typeof AbortController !== "undefined" ? new AbortController() : null;
  const t = ctrl ? setTimeout(() => ctrl.abort(), ms) : null;
  try {
    return await fetch(url, { cache: "no-store", ...init, signal: ctrl?.signal });
  } finally {
    if (t) clearTimeout(t);
  }
}

async function fetchJSON(url, ms) {
  const res = await fetchWithTimeout(url, ms);
  if (!res.ok) return null;
  return res.json();
}

// Prefer the gzip (≈3 MB vs ≈10 MB). It is stored as opaque bytes (no Content-Encoding),
// so we gunzip it here: DecompressionStream where available (Safari 16.4+, Chromium 80+),
// else fflate (lazy-loaded; iOS 15). Falls back to the plain JSON if anything goes wrong.
async function download(manifest, base) {
  if (manifest.url_gz) {
    try {
      const res = await fetchWithTimeout(new URL(manifest.url_gz, base).href, DOWNLOAD_TIMEOUT_MS,
        { cache: "default" });
      if (res.ok) {
        const bytes = await gunzip(new Uint8Array(await res.arrayBuffer()));
        return await parseVerified(bytes, manifest.sha256);
      }
    } catch (e) {
      if (e?.name === "IntegrityError") throw e;
      console.warn("gzip snapshot unavailable, using plain JSON:", e);
    }
  }
  const res = await fetchWithTimeout(new URL(manifest.url, base).href, DOWNLOAD_TIMEOUT_MS,
    { cache: "default" });
  if (!res.ok) throw new Error(`snapshot HTTP ${res.status}`);
  return parseVerified(new Uint8Array(await res.arrayBuffer()), manifest.sha256);
}

export async function gunzip(bytes) {
  if (typeof DecompressionStream !== "undefined" && typeof Response !== "undefined") {
    try {
      const stream = new Blob([bytes]).stream().pipeThrough(new DecompressionStream("gzip"));
      return new Uint8Array(await new Response(stream).arrayBuffer());
    } catch {
      /* fall through to fflate */
    }
  }
  const { gunzipSync } = await import("fflate");
  return gunzipSync(bytes);
}

async function parseVerified(bytes, expectedSha) {
  const subtle = typeof crypto !== "undefined" ? crypto.subtle : undefined;
  if (subtle && expectedSha) {
    const digest = await subtle.digest("SHA-256", bytes);
    const hex = [...new Uint8Array(digest)].map((b) => b.toString(16).padStart(2, "0")).join("");
    if (hex !== expectedSha) {
      const err = new Error(`sha256 mismatch: got ${hex.slice(0, 8)}, want ${expectedSha.slice(0, 8)}`);
      err.name = "IntegrityError";
      throw err;
    }
  }
  return JSON.parse(new TextDecoder("utf-8").decode(bytes));
}

export function validate(data, manifest) {
  if (!Array.isArray(data)) throw new Error("snapshot is not an array");
  if (manifest.entries && Math.abs(data.length - manifest.entries) > manifest.entries * 0.05) {
    throw new Error(`snapshot has ${data.length} entries, manifest says ${manifest.entries}`);
  }
  // A sanity floor: never replace the dictionary with a near-empty file.
  if (data.length < 1000) throw new Error(`snapshot has only ${data.length} entries`);
  for (const e of data) {
    for (const k of REQUIRED_KEYS) {
      if (!(k in e)) throw new Error(`entry missing ${k}`);
    }
  }
}

function announce(manifest, previous) {
  const s = manifest.diff_summary || {};
  const bits = [];
  if (s.roots_added) bits.push(`+${s.roots_added} roots`);
  if (s.words_added) bits.push(`+${s.words_added} words`);
  if (s.words_changed) bits.push(`${s.words_changed} corrections`);
  const what = previous?.dataVersion && bits.length ? ` (${bits.join(", ")})` : "";
  import("./utils")
    .then(({ toastNotice }) =>
      toastNotice?.("Dictionary updated", `Now on data ${manifest.version}${what}.`)
    )
    .catch(() => {});
}

function readLS(k) {
  try {
    return localStorage.getItem(k);
  } catch {
    return null;
  }
}
function writeLS(k, v) {
  try {
    localStorage.setItem(k, v);
  } catch {
    /* storage unavailable */
  }
}

/**
 * Schedules update checks: once after startup (idle) and whenever the native app
 * returns to the foreground. Returns a cleanup function.
 */
export function scheduleDictionaryUpdates() {
  let cancelled = false;
  const kick = () => {
    if (!cancelled) checkForDictionaryUpdate();
  };
  const idle = typeof window !== "undefined" && window.requestIdleCallback
    ? window.requestIdleCallback(kick, { timeout: 15000 })
    : setTimeout(kick, 5000);
  let listener = null;
  if (Capacitor.isNativePlatform()) {
    import("@capacitor/app")
      .then(({ App }) => App.addListener("resume", kick))
      .then((l) => {
        if (cancelled) l.remove();
        else listener = l;
      })
      .catch(() => {});
  }
  return () => {
    cancelled = true;
    if (typeof window !== "undefined" && window.cancelIdleCallback) window.cancelIdleCallback(idle);
    clearTimeout(idle);
    listener?.remove();
  };
}
